import { motion } from "framer-motion";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ChatCard from "../components/chat/ChatCard";
import dreamWorldProvider from "../DreamWorldProvider";
import { selectUserGlobalData } from "../store/dreamworldSlice";
import {
  selectEditingChats,
  selectHasMoreEditingChats,
  selectLastVisibleChat,
  addEditingChats,
  setHasMoreEditingChats,
  setLastVisibleChat,
  setEditingChats,
} from "../store/editingChatsSlice";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import { AppDispatch } from "../store/store";
import PageLayout from "../components/ui/PageLayout";

const EditingChatsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(selectUserGlobalData);
  const chats = useSelector(selectEditingChats);
  const hasMore = useSelector(selectHasMoreEditingChats);
  const lastVisible = useSelector(selectLastVisibleChat);

  const [loading, setLoading] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchChats = useCallback(async () => {
    if (userGlobalData) {
      setLoading(true);
      try {
        const { items, lastVisible: newLastVisible } =
          await dreamWorldProvider.getInteractiveEditings(
            userGlobalData.user.uid
          );
        dispatch(setEditingChats(items as any));
        dispatch(setLastVisibleChat(newLastVisible));
        dispatch(setHasMoreEditingChats(newLastVisible !== null));
      } catch (error) {
        console.error("Error fetching editing chats:", error);
      } finally {
        setLoading(false);
      }
    }
  }, [dispatch, userGlobalData]);

  const fetchMoreChats = useCallback(async () => {
    if (userGlobalData && hasMore && lastVisible) {
      setLoading(true);
      try {
        const { items, lastVisible: newLastVisible } =
          await dreamWorldProvider.getInteractiveEditings(
            userGlobalData.user.uid,
            lastVisible
          );
        dispatch(addEditingChats(items));
        dispatch(setLastVisibleChat(newLastVisible));
        dispatch(
          setHasMoreEditingChats(newLastVisible !== null && items.length !== 0)
        );
      } catch (error) {
        console.error("Error fetching more editing chats:", error);
      } finally {
        setLoading(false);
      }
    }
  }, [userGlobalData, hasMore, lastVisible, dispatch]);

  useEffect(() => {
    if (chats.length === 0) {
      fetchChats();
    }
  }, [fetchChats, chats.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchMoreChats();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [fetchMoreChats]);

  return (
    <div className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 min-h-screen text-white">
      <PageLayout>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-br from-purple-400 to-pink-400 bg-clip-text text-transparent py-3 px-1">
                Editing Chats
              </h1>
              <p className="text-gray-400 mt-2 text-sm sm:text-base">
                Continue your creative conversations
              </p>
            </div>
          </div>
        </motion.div>
        {/* <PhotoEffectsComponent /> */}

        <div className="grid grid-cols-1 gap-1">
          {chats.map((chat) => (
            <ChatCard key={chat.id} chat={chat} />
          ))}
        </div>

        <div
          ref={loadMoreRef}
          className="h-20 flex items-center justify-center"
        >
          {loading && <LoadingIndicator />}
          {!hasMore && chats.length > 0 && (
            <div className="text-gray-400 text-sm">All chats loaded.</div>
          )}
        </div>
      </PageLayout>
    </div>
  );
};

export default EditingChatsPage;
